import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, ApplicationDTO, ApplicationFilterDTO, FeatureDTO, UserGroupDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, IChipsSettingProps } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleMaintenance } from './AccessRoleMaintenance';
import { access } from 'fs';
import { useTranslation } from 'react-i18next';

export const AccessRole: React.FC = () => {

    const [userGroupList, setUserGroupList] = useState<UserGroupDTO[]>([]);
    const [applicationList, setApplicationList] = useState<ApplicationDTO[]>([]);
    const [featureList, setFeatureList] = useState<FeatureDTO[]>([]);
    const [accessRoleList, setAccessRoleList] = useState<AccessRoleDTO[]>([]);
    const [accessRoleFilterDTO, setAccessRoleFilterDTO] = useState<AccessRoleFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setAccessRoleFilterDTO({
            ...accessRoleFilterDTO,
            userGroupId: '',
            userId: '',
            applicationId: '',
            featureId: '',
        } as AccessRoleFilterDTO);
    }, []);

    useEffect(() => {
        handleApplicationGetAllAsync();
        handleUserGroupGetAllAsync();
        handleAccessRoleGetAllViewAsync();

        handleGetPermission(ListId.authsphere.accessRoleId, setAccessRolePermissionDTO);
    }, [accessRoleFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleAccessRoleGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleFeatureGetAllAsync();
    }, [accessRoleFilterDTO?.applicationId]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);

    const handleUserGroupGetAllAsync = useCallback(async () => {
        const userGroupFilter = {

        };
        GenericService.GetAllViewAsync1('authsphere.userGroup', userGroupFilter, setActionExecuted, setLoading, setUserGroupList, showMessage, true);
    }, []);

    const handleApplicationGetAllAsync = useCallback(async () => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            isLocal: true,
            companyId: '',
        };
        GenericService.GetAllViewAsync1('appsphere.application', applicationFilterDTO, setActionExecuted, setLoading, setApplicationList, showMessage, true);
    }, []);

    const handleFeatureGetAllAsync = useCallback(async () => {
        if(accessRoleFilterDTO?.applicationId === '')
            return;

        if(accessRoleFilterDTO?.applicationId === undefined)
            return;

        const featureFilter = {
            applicationId: accessRoleFilterDTO?.applicationId
        };

        GenericService.GetAllViewAsync1('appsphere.feature', featureFilter, setActionExecuted, setLoading, setFeatureList, showMessage, true);
    }, [accessRoleFilterDTO?.applicationId]);

    const handleAccessRoleGetAllViewAsync = useCallback(async () => {
        if (accessRoleFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView === undefined)
            return;
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage(t('acessRole.message.accessRolePermissionDTO'), 'info');
            return;
        }
        GenericService.GetAllViewAsync1('authsphere.accessRole', accessRoleFilterDTO, setActionExecuted, setLoading, setAccessRoleList, showMessage, true);
    }, [accessRoleFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setAccessRoleFilterDTO({
            ...accessRoleFilterDTO,
            userGroupId: '',
            userId: '',
            applicationId: '',
            featureId: '',
        } as AccessRoleFilterDTO);
    };

    function handleSearchClick() {
        handleAccessRoleGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('acessRole.message.handleNew'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('acessRole.message.handleViewDetatils'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('acessRole.message.handleDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: t('acessRole.message.newAcessRole'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('acessRole.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: t('acessRole.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('acessRole.chipFilters.totalRecords'), value: accessRoleList.length },
    ];

    const chipStatus : IChipsSettingProps[] = [
        { value: 'true', iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: 'false', iconName: 'block_outlined', iconColor: 'warning' },
    ];

    const columns: ICardColumns[] = [
        { field: 'application', label: t('acessRole.cardColumns.application'), size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'feature', label: t('acessRole.cardColumns.feature'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'userGroup', label: t('acessRole.cardColumns.userGroup'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'allowGetAllView', label: t('acessRole.cardColumns.allowGetAllView'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'allowGetById', label: t('acessRole.cardColumns.allowGetById'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'allowAdd', label: t('acessRole.cardColumns.allowAdd'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'allowUpdate', label: t('acessRole.cardColumns.allowUpdate'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'allowDelete', label: t('acessRole.cardColumns.allowDelete'), size: 1, type: 'boolean', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='COMPANY'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='applicationId'
                                label={t('acessRole.layoutDashboard.application')}
                                setValue={setAccessRoleFilterDTO}
                                value={accessRoleFilterDTO?.applicationId}
                                obj={accessRoleFilterDTO}
                                valueList={applicationList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='featureId'
                                label={t('acessRole.layoutDashboard.functionality')}
                                setValue={setAccessRoleFilterDTO}
                                value={accessRoleFilterDTO?.featureId}
                                obj={accessRoleFilterDTO}
                                valueList={featureList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='userGroupId'
                                label={t('acessRole.layoutDashboard.userGroup')}
                                setValue={setAccessRoleFilterDTO}
                                value={accessRoleFilterDTO?.userGroupId}
                                obj={accessRoleFilterDTO}
                                valueList={userGroupList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={accessRoleList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <AccessRoleMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <AccessRoleMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'authsphere.accessRole'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};
